import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { FiUpload } from 'react-icons/fi';
import ImageResize from 'quill-image-resize-module-react';
import '../../styles/custom-quill.css';

const BASE_URL = process.env.REACT_APP_BASE_URL;

Quill.register('modules/imageResize', ImageResize);

const AdminCreatePost = () => {
  const [type, setType] = useState('newsletter');
  const [writtenBy, setWrittenBy] = useState('');
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [titleImage, setTitleImage] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  
  const editorRef = useRef(null);
  const quillRef = useRef(null);
  const navigate = useNavigate();

  const uploadImage = async (file) => {
    // This is a placeholder function - implement your actual image upload logic
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve(e.target.result);
      };
      reader.readAsDataURL(file);
    });
  };

  const imageHandler = async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      if (input.files != null && input.files[0] !== undefined) {
        const file = input.files[0];
        try {
          const imageUrl = await uploadImage(file);
          const range = quillRef.current.getSelection(true);
          
          // Insert a new line before the image
          quillRef.current.insertText(range.index, '\n');
          
          // Insert the image
          quillRef.current.insertEmbed(range.index + 1, 'image', imageUrl);
          
          // Insert a new line after the image
          quillRef.current.insertText(range.index + 2, '\n');
          
          // Move cursor after the image and new line
          quillRef.current.setSelection(range.index + 3, 0);
        } catch (error) {
          console.error('Error uploading image:', error);
        }
      }
    };
  };

  useEffect(() => {
    if (editorRef.current && !quillRef.current) {
      quillRef.current = new Quill(editorRef.current, {
        theme: 'snow',
        modules: {
          imageResize: {
            displaySize: true,
            modules: ['Resize', 'DisplaySize']
          },
          toolbar: {
            container: [
              [{ 'header': [1, 2, 3, false] }],
              ['bold', 'italic', 'underline', 'strike'],
              [{ 'color': [] }, { 'background': [] }],
              [{ 'align': [] }],
              [{ 'list': 'ordered'}, { 'list': 'bullet' }],
              ['link', 'image'],
              ['clean']
            ],
            handlers: {
              image: imageHandler
            }
          },
          clipboard: {
            matchVisual: false
          }
        },
        placeholder: 'Start writing your content...',
      });
      
      // Apply custom styles to Quill editor
      const editor = editorRef.current.querySelector('.ql-editor');
      editor.style.minHeight = '400px';
      editor.style.fontSize = '16px';
      editor.style.lineHeight = '1.8';
      editor.style.padding = '2rem';
      
      quillRef.current.on('text-change', () => {
        setContent(quillRef.current.root.innerHTML);
      });
    }
  }, []);

  const handleTitleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const imageUrl = await uploadImage(file);
        setTitleImage(imageUrl);
      } catch (error) {
        console.error('Error uploading title image:', error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const post = { 
      type, 
      writtenBy, 
      content,
      title,
      titleImage,
      youtubeUrl: type === 'video' ? youtubeUrl : undefined 
    };

    try {
      const response = await fetch(`${BASE_URL}/api/admin/createpost`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify(post)
      });
      if (response.ok) {
        navigate('/admin/dashboard');
      }
    } catch (error) {
      console.error('Error creating post:', error);
    }
  };

  const postTypes = ['blog', 'newsletter','tenant-stories', 'video'];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto p-8 mb-16 bg-white shadow-2xl rounded-2xl">
        <h2 className="text-4xl font-bold mb-10 text-center text-gray-800">
          Create {type === 'blog' ? 'Blog Post' : type === 'newsletter' ? 'Newsletter' : type === 'video' ? 'Video Post' : 'Tenant Story'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Replace radio buttons with dropdown */}
          <div>
            <label className="block font-medium mb-3 text-gray-700">Post Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                        transition-all duration-200 capitalize"
            >
              {postTypes.map((postType) => (
                <option key={postType} value={postType} className="capitalize">
                  {postType.replace('-', ' ')}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <label className="block  font-medium mb-3 text-gray-700">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                          transition-all duration-200"
                required
              />
            </div>
            <div>
              <label className="block  font-medium mb-3 text-gray-700">Written By</label>
              <input
                type="text"
                value={writtenBy}
                onChange={(e) => setWrittenBy(e.target.value)}
                className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                          transition-all duration-200"
                required
              />
            </div>
          </div>

          <div className="space-y-3">
            <label className="block  font-medium text-gray-700">Title Image</label>
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <input
                  type="text"
                  value={titleImage}
                  onChange={(e) => setTitleImage(e.target.value)}
                  className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                            transition-all duration-200"
                  placeholder="Image URL or upload"
                  required
                />
              </div>
              <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 p-4 rounded-xl transition-all duration-200">
                <FiUpload className="w-6 h-6" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleTitleImageUpload}
                  className="hidden"
                />
              </label>
            </div>
            {titleImage && (
              <img src={titleImage} alt="Title preview" className="h-48 w-full object-cover rounded-xl border" />
            )}
          </div>

          {type === 'video' && (
            <div>
              <label className="block font-medium mb-3 text-gray-700">YouTube URL</label>
              <input
                type="url"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                          transition-all duration-200"
                placeholder="Enter YouTube video URL"
                required={type === 'video'}
                pattern="^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)[\w-]+"
              />
            </div>
          )}

          <div className="">
            <label className="block text-2xl mb-4 font-medium text-gray-700">Content</label>
            <div ref={editorRef} className="bg-white border border-gray-300 shadow-sm"></div>
          </div>

          <button
            type="submit"
            className="w-full py-5 bg-indigo-600 text-white text-xl font-semibold rounded-xl
                     hover:bg-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            Publish {type === 'blog' ? 'Blog Post' : type === 'newsletter' ? 'Newsletter' : type === 'video' ? 'Video Post' : 'Tenant Story'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminCreatePost;
